import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { MagnifyingGlass, Cube, ClipboardText, Buildings, Users } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Traceability() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("box") || "";
  const [boxNo, setBoxNo] = useState(initial);
  const [data, setData] = useState(null);
  const [contrib, setContrib] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (val) => {
    const v = (val ?? boxNo).trim();
    if (!v) return;
    setLoading(true);
    setData(null);
    setContrib(null);
    try {
      const { data } = await api.get(`/trace/box/${encodeURIComponent(v)}`);
      setData(data);
      setParams({ box: v });
      const runId = data?.assembly_run?.id || data?.box?.assembly_run_id;
      if (runId) {
        try {
          const c = await api.get(`/assembly-runs/${runId}/contributions`);
          setContrib(c.data);
        } catch { /* not fatal */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) lookup(initial);
    // eslint-disable-next-line
  }, []);

  return (
    <div className="space-y-6" data-testid="trace-page">
      <div>
        <h1 className="font-display text-4xl uppercase tracking-tighter">Traceability</h1>
        <p className="text-xs text-zinc-600 mt-1">Scan a box QR or enter the box number to view its full route card</p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); lookup(); }}
        className="flex gap-3 items-end max-w-2xl"
        data-testid="trace-form"
      >
        <div className="flex-1">
          <label className="brut-label">Box Number</label>
          <input
            value={boxNo}
            onChange={(e) => setBoxNo(e.target.value)}
            placeholder="BOX-26-00001"
            className="brut-input mt-1"
            data-testid="trace-input"
          />
        </div>
        <button type="submit" disabled={loading} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="trace-lookup-btn">
          <MagnifyingGlass size={14} weight="bold" /> {loading ? "Looking..." : "Lookup"}
        </button>
      </form>

      {data && (
        <div className="space-y-6" data-testid="trace-result">
          {/* Header */}
          <div className="brut-card p-6">
            <div className="flex justify-between items-start gap-4 flex-wrap">
              <div>
                <div className="brut-tag brut-tag-yellow mb-2">Box</div>
                <h2 className="font-display text-3xl uppercase tracking-tight">{data.box.box_number}</h2>
                <div className="font-mono-ui text-sm text-zinc-700 mt-1">
                  {data.box.product_part_number ? `${data.box.product_part_number} · ` : ""}
                  {data.box.product_name} · {data.box.units_count} units
                </div>
                {data.box.assembly_run_number && (
                  <div className="text-xs mt-1">
                    Assembly Run: <Link to="/assembly-runs" className="font-bold underline">{data.box.assembly_run_number}</Link>
                  </div>
                )}
                <div className="text-xs text-zinc-500 mt-1">
                  Assembled: {new Date(data.box.created_at).toLocaleString()} · by {data.box.created_by_name}
                </div>
              </div>
              <QRCodeSVG value={`${window.location.origin}/trace?box=${data.box.box_number}`} size={100} bgColor="#fff" fgColor="#1e293b" />
            </div>
          </div>

          {/* Route Card */}
          <div className="brut-card p-6">
            <div className="font-display text-lg uppercase tracking-tight mb-3 flex items-center gap-2">
              <Cube size={18} weight="bold" /> Route Card · Box → Components → Batches → Suppliers
            </div>

            <div className="space-y-3">
              {(data.assembly_run?.components_consumed?.length ? data.assembly_run.components_consumed : data.box.components || []).map((c, i) => {
                const grn = data.grns[c.grn_id];
                const isProduction = grn?.kind === "production";
                return (
                  <div key={i} className="brut-border p-3" data-testid={`route-comp-${i}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <div className="brut-label">Component</div>
                        <div className="font-bold font-mono-ui">{c.part_number}</div>
                        <div className="text-xs">{c.part_name}</div>
                        <span className="brut-tag brut-tag-yellow mt-1 inline-block">{c.part_category}</span>
                      </div>
                      <div>
                        <div className="brut-label">Qty Used</div>
                        <div className="font-mono-ui text-lg font-bold">{c.qty} {c.unit}</div>
                        <div className="text-xs text-zinc-600 mt-1">
                          {isProduction ? "Produced in run " : "From GRN batch "}
                          <Link to={isProduction ? "/productions" : `/grns/${c.grn_id}`} className="underline font-bold">{c.grn_number}</Link>
                        </div>
                      </div>
                      <div>
                        <div className="brut-label flex items-center gap-1"><Buildings size={10} weight="bold" /> Source</div>
                        <div className="font-display uppercase text-sm">{isProduction ? "Internal Manufacturing" : c.supplier_name}</div>
                        <div className="text-xs">{isProduction ? "Produced" : "Received"}: <span className="font-mono-ui">{c.received_date}</span></div>
                        {grn && !isProduction && (
                          <div className="text-xs text-zinc-600 mt-1">Invoice: {grn.invoice_number} ({grn.invoice_date})</div>
                        )}
                      </div>
                    </div>

                    {isProduction && grn?.components_consumed?.length > 0 && (
                      <div className="mt-3 pl-4 border-l-4 border-[#60a5fa] bg-[#eff6ff] p-3">
                        <div className="brut-label mb-2">↳ Sub-Assembly Trace ({grn.components_consumed.length} raw inputs)</div>
                        <table className="brut-table">
                          <thead>
                            <tr><th>Part #</th><th>Name</th><th>Qty</th><th>Source GRN</th><th>Supplier</th></tr>
                          </thead>
                          <tbody>
                            {grn.components_consumed.map((cc, j) => (
                              <tr key={j} data-testid={`sub-trace-${i}-${j}`}>
                                <td className="font-bold font-mono-ui text-xs">{cc.part_number}</td>
                                <td className="text-xs">{cc.part_name}</td>
                                <td className="font-mono-ui text-xs">{cc.qty} {cc.unit}</td>
                                <td className="font-mono-ui text-xs">{cc.grn_number}</td>
                                <td className="text-xs">{cc.supplier_name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Built by — batch-level worker attribution */}
          {contrib && contrib.rows && contrib.rows.length > 0 && (
            <div className="brut-card p-6" data-testid="trace-built-by">
              <div className="font-display text-lg uppercase tracking-tight mb-2 flex items-center gap-2">
                <Users size={18} weight="bold" /> Built By
              </div>
              <div className="text-xs text-zinc-600 mb-3">
                Workers who contributed to the assembly run <b>{data.box.assembly_run_number}</b>.
              </div>
              <table className="brut-table">
                <thead><tr><th>Worker</th><th>Role</th><th>Operations</th><th>Days</th><th className="text-right">Pieces</th><th className="text-right">Share</th></tr></thead>
                <tbody>
                  {contrib.rows.map((r) => (
                    <tr key={r.staff_id}>
                      <td className="font-bold">{r.staff_name}</td>
                      <td><span className="brut-tag brut-tag-yellow text-[10px]">{r.designation}</span></td>
                      <td className="font-mono-ui text-xs">{r.operations.join(", ")}</td>
                      <td className="font-mono-ui text-xs">{r.sessions.length}</td>
                      <td className="text-right font-mono-ui font-bold">{r.produced}</td>
                      <td className="text-right font-mono-ui">{contrib.cumulative > 0 ? Math.round((r.produced / contrib.cumulative) * 100) + "%" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Packing list link */}
          {data.packing_list && (
            <div className="brut-card p-6">
              <div className="font-display text-lg uppercase tracking-tight mb-2 flex items-center gap-2">
                <ClipboardText size={18} weight="bold" /> Shipped In
              </div>
              <div className="font-mono-ui text-sm">
                <Link to={`/packing-lists/${data.packing_list.id}`} className="underline font-bold">{data.packing_list.pl_number}</Link>
                {" · "}{data.packing_list.customer} · Ship date {data.packing_list.ship_date}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                {data.packing_list.total_boxes} boxes · {data.packing_list.total_units} total units
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
